/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-01-10 00:00:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, reset, getFormValues, change } from 'redux-form';
import {
  Page,
  Layout,
  Card,
  Button,
  Stack,
  PageActions,
  SkeletonBodyText,
  TextContainer,
  SkeletonDisplayText
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import MainMenu from 'src/components/containers/main-menu';
import PremiumLockCard from 'src/components/views/tools/variants-display/premium-lock-card';
import ProcessAddon from 'src/components/views/subscription-v3/addon/process-addon';

import { URL } from 'src/constants/links';
import DidYouKnow from 'src/components/views/tools/variants-display/did-you-know';
import { ADDONS_INFO, VARIANT_DISPLAY_NAME } from 'src/constants/subscription';
import VariantsDisplayInputForm, {
  OPEN_NO_SUPPORT_ISW_KEY
} from 'src/components/views/tools/variants-display/form';
import EffectMerchandisingRuleModal from 'src/components/views/tools/variants-display/effect-merchandising-rule';

import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import ProcessingUpdateBanner from 'src/components/views/common/processing-update-banner';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import SendRequestModal from 'src/components/containers/common/send-request-modal';

import {
  buildInitialVariantsDisplayValues,
  buildCollections,
  buildOptions,
  validateVariantDisplayValues,
  buildVariantsDisplayValues
} from 'src/helpers/variants-display-helper';

import * as FilterDataActions from 'actions/filter-data-actions';
import * as variantsDisplayActions from 'src/stores/actions/variants-display-actions';
import NoSupportISWModal from 'src/components/views/tools/variants-display/no-support-isw';
import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import TitleHelp from 'src/components/views/common/title-help';
import moment from 'moment/moment';
import { RELEASE_0423_DAY } from 'src/constants/campaigns';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

type Props = {
  initialValues: Object,
  enableVariantsDisplay: boolean,
  collections: Array<Object>,
  selectedCollections: Array<Object>,
  optionsList: Array<Object>,
  selectedOptions: Array<Object>,
  isExistsMERProductId: boolean,
  isBetaUser: boolean,

  getFilterData: () => void,
  getVariantsDisplay: () => void,
  updateVariantsDisplay: () => void,
  resetForm: () => void,
  checkExistsMERProductId: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  selectedId: number,
  activeRequestModal: boolean,
  activeModalResult: boolean,
  activeModalSave: boolean,
  saveDate: Object,
  openNoSupportISW: boolean,
  activeModalStatus: boolean
};

class VariantsDisplay extends Component<ComposedProps, State> {
  state = {
    selectedId: -1,
    activeRequestModal: false,
    activeModalResult: false,
    activeModalSave: false,
    saveDate: {},
    openNoSupportISW: false,
    modalAddon: false
  };

  componentDidMount() {
    const {
      getFilterData,
      getVariantsDisplay,
      checkExistsMERProductId,
      getAddonStatus,
      invoice,
      getDetailInvoice
    } = this.props;

    // Get detail invoice
    if (_.isEmpty(invoice) || !invoice) {
      getDetailInvoice();
    }
    getAddonStatus(VARIANT_DISPLAY_NAME);

    getFilterData();
    getVariantsDisplay();
    checkExistsMERProductId();
  }

  componentWillReceiveProps(nextProps) {
    const { saving, deleting, scheduleSyncIn, waitSyncAt } = nextProps;
    const {
      saving: savingPrev,
      deleting: deletingPrev,
      updateShowBanner,
      getSyncProcessStatus
    } = this.props;

    // Check to show banner Processing update when change status/ import
    if ((savingPrev && !saving) || (deletingPrev && !deleting)) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }

      // Call sync process status
      getSyncProcessStatus();
    }
  }

  render() {
    const {
      i18n,
      submitting,
      handleSubmit,
      pristine,
      dirty,
      loading,
      updating,
      collections,
      selectedCollections,
      optionsList,
      selectedOptions,
      addonStatus,
      gettingAddonStatus,
      statusStore,
      subscribedVariantDisplayAddon,
      syncDuration,
      showScheduleSync,
      enableVariantsDisplay,
      history,
      tenantId,
      notAllowAddon,
      merchandisingV2,
      appliesToInstantSearchWidget,
      recentInstalledAt,
      isSavedEnable,
      userAppVersion
      // currentTotalProducts
    } = this.props;

    const { activeRequestModal } = this.state;

    const selectedAllCollections = _.find(selectedCollections, { value: 'all' });
    const enableSelectAllCollection = !_.isEmpty(selectedAllCollections);

    const selectedAllOptions = _.find(selectedOptions, { value: 'all' });
    const enableSelectAllOption = !_.isEmpty(selectedAllOptions);

    const isNewStore = !moment(new Date(recentInstalledAt * 1000)).isBefore(
      new Date(RELEASE_0423_DAY)
    );

    // Call get flag addon variant display and status here
    // Ex status = [variantDisplayTrial, subscribed, restoreVariantDisplay]
    // const oldStoreUsedVariantDisplay = userAppVersion !== 'v3' && currentTotalProducts <= 5000;
    const oldStoreUsedVariantDisplay = userAppVersion !== 'v3';

    const addonRemainingCycles =
      _.get(addonStatus, 'trialInfo.remainingDays', null) ||
      _.get(addonStatus, 'trialInfo.remainingDays', null) === 0
        ? _.get(addonStatus, 'trialInfo.remainingDays', null)
        : oldStoreUsedVariantDisplay
        ? 0
        : 14;

    const addonVariantDisplayStatus = subscribedVariantDisplayAddon
      ? 'subscribed'
      : !['trial', 'expiredSoon'].includes(statusStore) && addonRemainingCycles <= 0
      ? 'restoreVariantDisplay'
      : 'variantDisplayTrial';

    const docUrl = merchandisingV2
      ? URL.Link_V2_Tool_VariantDisplay_ManualGuide
      : URL.Link_V1_Tool_VariantDisplay_ManualGuide;

    return (
      <div className="BC-Page BC-Page-Variants-Display">
        <MainMenu menuId="menu-settings-variants-display" />
        <Page
          title={i18n.translate('AdminHTML.VariantsDisplay.VariantsDisplay.title')}
          primaryAction={
            !loading &&
            !gettingAddonStatus &&
            subscribedVariantDisplayAddon && (
              <div className="BC-Page BC-Page__Button">
                <Stack>
                  {!pristine && (
                    <Button disabled={submitting} onClick={this.discardChange}>
                      {i18n.translate('AdminHTML.Common.Action.cancel')}
                    </Button>
                  )}
                  <Button
                    primary
                    onClick={handleSubmit(this.previewSave.bind(this))}
                    disabled={pristine || submitting}
                    loading={updating && !pristine}>
                    {i18n.translate('AdminHTML.Common.Action.save')}
                  </Button>
                </Stack>
              </div>
            )
          }>
          <TitleHelp i18n={i18n} hrefUserManual={docUrl} />
          {loading || gettingAddonStatus ? (
            <Layout>
              <Layout.Section />
              <Layout.Section>
                <Card>
                  <Card.Section>
                    <TextContainer>
                      <SkeletonDisplayText size="small" />
                      <SkeletonBodyText />
                    </TextContainer>
                  </Card.Section>
                </Card>
              </Layout.Section>
            </Layout>
          ) : !subscribedVariantDisplayAddon ? (
            <PremiumLockCard
              disableLockUnLock={notAllowAddon}
              addonVariantDisplayStatus={addonVariantDisplayStatus}
              actionLabel={i18n.translate(`AdminHTML.Common.Action.unlockFeatureForTrial`)}
              action={() => {
                this.toggleModal('modalAddon');

                mixpanelV3Helper.trackOpenSubscriptionReview(
                  VARIANT_DISPLAY_NAME,
                  window.location.pathname,
                  i18n.translate(`AdminHTML.Common.Action.unlockFeatureForTrial`)
                );
              }}
            />
          ) : (
            <>
              <Layout>
                <Layout.Section>
                  <div className="BC-Container__Notifications">
                    {/* Installation reminder status banner */}
                    <InstallationRemindStatusBanner />

                    {/* Did you know? */}
                    <DidYouKnow
                      sendRequest={() => this.toggleModal('activeRequestModal')}
                      isNewStore={isNewStore}
                      isEnable={isSavedEnable}
                      docUrl={docUrl}
                    />

                    {/* Sticky notification for exceeded plan */}
                    <ExceededPlanNotification />
                  </div>
                </Layout.Section>

                {/* Processing update banner */}
                {showScheduleSync ? (
                  <Layout.Section>
                    <ProcessingUpdateBanner
                      syncDuration={syncDuration}
                      featureName="variantsDisplay"
                    />
                  </Layout.Section>
                ) : (
                  ''
                )}

                {/* Input form */}
                <Layout.Section>
                  <Card>
                    <Card.Section>
                      <VariantsDisplayInputForm
                        loading={loading}
                        enableVariantsDisplay={enableVariantsDisplay}
                        collections={buildCollections(collections, enableSelectAllCollection, i18n)}
                        onChangeCollection={this.onChangeCollection}
                        selectedCollections={selectedCollections}
                        optionsList={buildOptions(optionsList, enableSelectAllOption, i18n)}
                        onChangeOption={this.onChangeOption}
                        selectedOptions={selectedOptions}
                        merchandisingV2={merchandisingV2}
                        onOpenNoSupportISW={this.onOpenNoSupportISW.bind(this)}
                        appliesToInstantSearchWidget={appliesToInstantSearchWidget}
                        tenantId={tenantId}
                      />

                      {subscribedVariantDisplayAddon && <div style={{ height: '94px' }} />}
                    </Card.Section>
                  </Card>
                </Layout.Section>
              </Layout>

              <NoSupportISWModal
                active={this.state.openNoSupportISW}
                onCancel={() => this.setState({ openNoSupportISW: false })}
              />

              <SendRequestModal
                active={activeRequestModal}
                handleChange={() => this.toggleModal('activeRequestModal')}
              />

              <EffectMerchandisingRuleModal
                active={this.state.activeModalSave}
                onCancel={() => this.setState({ activeModalSave: false })}
                onSave={() => {
                  this.handleSave(this.state.saveDate);
                  this.setState({ activeModalSave: false });
                }}
              />

              <PageActions
                primaryAction={{
                  content: i18n.translate('AdminHTML.Common.Action.save'),
                  onAction: handleSubmit(this.previewSave.bind(this)),
                  disabled: pristine || submitting,
                  loading: updating && !pristine
                }}
                secondaryActions={
                  !pristine
                    ? [
                        {
                          content: i18n.translate('AdminHTML.Common.Action.cancel'),
                          onAction: this.discardChange,
                          disabled: submitting
                        }
                      ]
                    : []
                }
              />
            </>
          )}
        </Page>

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={dirty}
        />

        {/* Unlock addon to using feature */}

        <ProcessAddon
          openModalReview={this.state.modalAddon}
          addon={ADDONS_INFO.find(e => e.addonName === VARIANT_DISPLAY_NAME)}
          hadSubscribed={addonVariantDisplayStatus !== 'variantDisplayTrial'}
          handleAfterSubscribeAddon={this.handleCloseStatus}
          handleReviewModal={() => this.toggleModal('modalAddon')}
        />
      </div>
    );
  }

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Handle discard button
  discardChange = () => {
    const { resetForm } = this.props;

    resetForm();
  };

  handleSave = async data => {
    const { updateVariantsDisplay, merchandisingV2 } = this.props;

    // Build data package
    const saveData = buildVariantsDisplayValues(data, merchandisingV2);
    await updateVariantsDisplay(saveData);

    Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
  };

  previewSave = data => {
    const { isExistsMERProductId, i18n } = this.props;

    // Validate data
    validateVariantDisplayValues(data, i18n);

    if (isExistsMERProductId) {
      this.setState({ activeModalSave: true, saveDate: data });
    } else {
      this.handleSave.bind(this)(data);
    }
  };

  onOpenNoSupportISW() {
    const { tenantId } = this.props;
    const key = `${tenantId}:${OPEN_NO_SUPPORT_ISW_KEY}`;
    try {
      const timeOpenNoSupportISW = localStorage.getItem(key);
      this.setState({ openNoSupportISW: true });
      if (!timeOpenNoSupportISW) {
        localStorage.setItem(key, new Date().getTime());
      }
    } catch (error) {}
  }

  onChangeOption = async selected => {
    const { updateField } = this.props;

    // Detect having Select all option
    const allOption = _.find(selected, { value: 'all' });
    if (!_.isEmpty(allOption)) {
      // Reset selected fields
      updateField('options', []);
    }
  };

  onChangeCollection = async (field, selected) => {
    const { updateField } = this.props;

    // Detect having Select all option
    const allOption = _.find(selected, { value: 'all' });
    if (!_.isEmpty(allOption)) {
      // Reset selected fields
      updateField('collections', []);
    }
  };

  //Reload page after subscribe
  handleCloseStatus = () => {
    this.props.getAddonStatus(VARIANT_DISPLAY_NAME);
  };
}

const VariantsDisplayForm = reduxForm({
  form: 'VariantsDisplayForm', // a unique identifier for this form
  enableReinitialize: true
})(VariantsDisplay);

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    // Get current status addon sync
    getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n)),
    getAddonStatus: addonName => dispatch(subscriptionV3Actions.getAddonStatus(addonName)),
    getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),
    getVariantsDisplay: () => dispatch(variantsDisplayActions.getVariantsDisplay(ownProps.i18n)),
    updateVariantsDisplay: variantsDisplay =>
      dispatch(variantsDisplayActions.updateVariantsDisplay(variantsDisplay, '', ownProps.i18n)),
    updateField: (field, data) => dispatch(change(`VariantsDisplayForm`, field, data)),
    resetForm: () => dispatch(reset('VariantsDisplayForm')),
    checkExistsMERProductId: () =>
      dispatch(variantsDisplayActions.checkExistsMERProductId(ownProps.i18n))
  };
};

const mapStateToProps = (state, props) => {
  const { variantsDisplay = {} } = state;
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const optionsList = _.get(state.filterData, 'filterData.optionsList', []);
  const collections = _.get(state.filterData, 'filterData.collections', []);
  const subscriptionV3 = _.get(tenantApp, 'settings.subscriptionV3.settings.enable', false);
  const userAppVersion = _.get(tenantApp, 'userAppVersion', '');
  // const currentTotalProducts = _.get(tenantApp, 'appParams.currentTotalProducts', 0);
  const hasAddon = _.get(
    state.oauth.tenantApp,
    'settings.variantsDisplay.settings.variant-display',
    false
  );
  const merchandisingV2 =
    _.get(tenantApp, 'settings.visualMerchandising.settings.enable', false) ||
    _.get(tenantApp, 'adminAppFeatures.merchandisingV2', false);
  const isBetaUser = _.get(tenantApp, 'settings.betaLaunchCampaign.settings.enable', false);
  const variantsDisplaySettings = _.get(variantsDisplay, 'variantsDisplay', {});
  const addonStatus = _.get(state.subscriptionV3, 'addonStatus', {});
  const initialValues = buildInitialVariantsDisplayValues(
    variantsDisplaySettings,
    optionsList,
    props.i18n,
    merchandisingV2,
    hasAddon,
    subscriptionV3
  );
  const formValues = getFormValues('VariantsDisplayForm')(state);
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', {});

  return {
    initialValues,
    enableVariantsDisplay: _.get(formValues, 'enable', false),
    collections,
    selectedCollections: _.get(formValues, 'collections', []),
    optionsList,
    selectedOptions: _.get(formValues, 'options', []),
    loading: _.get(variantsDisplay, 'getLoading', false),
    updating: _.get(variantsDisplay, 'updateLoading', false),
    merchandisingV2,
    isExistsMERProductId: _.get(variantsDisplay, 'isExistsMERProductId', false),
    isBetaUser,
    appliesToInstantSearchWidget: _.get(
      variantsDisplaySettings,
      'appliesTo.instantSearchWidget',
      false
    ),
    addonStatus,
    gettingAddonStatus: _.get(state.subscriptionV3, 'loading', false),
    tenantId,
    statusStore: _.get(state.oauth, 'subscription.status', ''),
    notAllowAddon: _.get(state.oauth, 'subscription.notAllowAddon', false),
    subscribedVariantDisplayAddon: hasAddon,
    recentInstalledAt: _.get(state.oauth, 'tenantApp.recentInstalledAt', ''),
    isSavedEnable: variantsDisplaySettings.enable,
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    userAppVersion
    // currentTotalProducts
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(VariantsDisplayForm)),
  'MapProduct'
);
